import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { StateContext } from '../../utils/context';
import { getCookie } from '../../jwt/_helpers';
import Select from 'react-select';
import { getUser } from '../../utils/getUser';
import { FormText, Label, Card, CardBody, CardTitle, Button } from 'reactstrap';
import BackupCodes from '../../components/backupCodes';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const TwoFactorAuth = () => {
  const token = getCookie("token");
  const { currentUser, setCurrentUser } = useContext(StateContext);
  const { t } = useTranslation();
  const options = {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-api-key": X_API_KEY,
      "Content-Type": "application/json"
    }
  };

  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [currentTwoFactorMethod, currentTwoFactorMethodSET] = useState({ value: "", label: t("twoFactorAuth.view.form.option.2faMethod.selectMethod") });
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [hasChangesSaved, setHasChangesSaved] = useState(false);

  const TWO_FACTOR_METHOD_OPTIONS = [
    { value: "email", label: t("twoFactorAuth.view.form.option.2faMethod.email") },
    { value: "auth", label: t("twoFactorAuth.view.form.option.2faMethod.auth") },
  ];

  useEffect(() => {
    if (currentUser && currentUser.two_factor_method) {
      const selectedMethod = TWO_FACTOR_METHOD_OPTIONS.find(
        option => option.value === currentUser.two_factor_method
      );
      currentTwoFactorMethodSET(selectedMethod);
      setSuccess(true);
    }
  }, [currentUser]);

  useEffect(() => {
    getUser(token, currentUser, setCurrentUser);
  }, [success]);

  useEffect(() => {
    if (currentTwoFactorMethod.value === "auth") {
      axios.get(`${API_URL}generate-2fa-qr-code/`, { ...options, responseType: "arraybuffer" })
        .then(response => {
          const qrCodeBuffer = response.data;
          const base64QrCode = Buffer.from(qrCodeBuffer, "binary").toString("base64");
          setQrCodeUrl(`data:image/png;base64,${base64QrCode}`);
        })
        .catch(error => {
          console.error('Error fetching QR code:', error);
        });
    }
  }, [currentTwoFactorMethod]);

  const handleSave = () => {
    setIsSaving(true);
    axios({
      method: "PATCH",
      url: `${API_URL}users/${currentUser.id}/`,
      data: { "two_factor_method": selectedMethod.value },
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${token}`,
        "x-api-key": X_API_KEY,
      }
    })
      .then(response => {
        if (response.status === 200) {
          axios({
            method: "GET",
            url: `${API_URL}users/${currentUser.id}/`,
            mode: 'cors',
            headers: {
              Authorization: `Bearer ${token}`,
              "x-api-key": X_API_KEY,
            }
          }).then(response => {
            if (response.status === 200) {
              setCurrentUser(response.data);
              setSuccess(true);
              currentTwoFactorMethodSET(selectedMethod);
              setSelectedMethod(null);
              setHasChangesSaved(true);
            }
            setIsSaving(false);
          });
        }
      })
      .catch(error => {
        console.error('Error updating Two-Factor Method:', error);
        setIsSaving(false);
      });
  };

  return (
    <Card>
      <CardBody>
        <CardTitle className="d-flex justify-content-between align-items-center">
          <h2 className="mb-0">{t("twoFactorAuth.view.title")}</h2>
          <div className="mt-4">
            <BackupCodes />
          </div>
        </CardTitle>
        <div>
          <Label>{t("twoFactorAuth.view.form.label.currentMethod")}</Label>
          <Select
            components={{ IndicatorSeparator: () => null }}
            name="twoFactorMethod"
            options={TWO_FACTOR_METHOD_OPTIONS}
            value={selectedMethod || currentTwoFactorMethod}
            onChange={(e) => {
              setSelectedMethod(e);
              setHasChangesSaved(false);
            }}
          />
          {
            currentTwoFactorMethod.value === "email" &&
            <FormText color="muted">
                {t("twoFactorAuth.view.form.help_text.currentMethod")}
            </FormText>
          }
        </div>

        {selectedMethod && selectedMethod.value !== currentTwoFactorMethod.value && (
          <Button className="mt-2 btn btn-success float-right" style={{width: "100px"}} onClick={handleSave} disabled={isSaving}>
            {isSaving ? t("twoFactorAuth.view.form.button.saving") : t("twoFactorAuth.view.form.button.save")}
          </Button>
        )}

        {(hasChangesSaved || selectedMethod?.value === currentTwoFactorMethod.value || success) && (
          <div className="mt-3">
            {currentTwoFactorMethod.value === "auth" ? (
              <div>
                {qrCodeUrl ? (
                  <div>
                    <h6>{t("twoFactorAuth.view.form.label.authenticatorSetupTitle")}</h6>
                    <p>{t("twoFactorAuth.view.form.label.scanInstructions")}</p>
                    <img src={qrCodeUrl} alt="QR Code for authenticator setup" />
                    <ol>
                      <li>{t("twoFactorAuth.view.form.label.step1")}</li>
                      <li>{t("twoFactorAuth.view.form.label.step2")}</li>
                      <li>{t("twoFactorAuth.view.form.label.step3")}</li>
                      <li>{t("twoFactorAuth.view.form.label.step4")}</li>
                      <li>{t("twoFactorAuth.view.form.label.step5")}</li>
                    </ol>
                    <p>
                      {t("twoFactorAuth.view.form.label.appLinks")}
                      <ul>
                        <li>
                          <b>{t("twoFactorAuth.view.form.label.appLinksAuthy")}</b><br/>
                          <a href="https://play.google.com/store/apps/details?id=com.authy.authy" target="_blank" rel="noopener noreferrer">{t("twoFactorAuth.view.form.label.googlePlay")}</a> | <a href="https://apps.apple.com/us/app/twilio-authy/id494168017" target="_blank" rel="noopener noreferrer">{t("twoFactorAuth.view.form.label.appStore")}</a>
                        </li>
                        <li>
                          <b>{t("twoFactorAuth.view.form.label.appLinksGoogle")}</b><br/>
                          <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer">{t("twoFactorAuth.view.form.label.googlePlay")}</a> | <a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank" rel="noopener noreferrer">{t("twoFactorAuth.view.form.label.appStore")}</a>
                        </li>
                        <li>
                          <b>{t("twoFactorAuth.view.form.label.appLinksMicrosoft")}</b><br/>
                          <a href="https://play.google.com/store/apps/details?id=com.azure.authenticator" target="_blank" rel="noopener noreferrer">{t("twoFactorAuth.view.form.label.googlePlay")}</a> | <a href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458" target="_blank" rel="noopener noreferrer">{t("twoFactorAuth.view.form.label.appStore")}</a>
                        </li>
                      </ul>
                    </p>
                  </div>
                ) : (
                  <p>{t("twoFactorAuth.view.form.label.loadingQrCode")}</p>
                )}
              </div>
            ) : (
              <div className="mt-3">
                <h6>{t("twoFactorAuth.view.form.label.emailSetupTitle")}</h6>
                <p>
                  {t("twoFactorAuth.view.form.label.emailDescription")}
                </p>
              </div>
            )}
          </div>
        )}        
      </CardBody>
    </Card>
  );
};

export default TwoFactorAuth;